import { Suspense, useEffect, useMemo } from "react";
import { useQueryLoader } from "react-relay";

import { Loader } from "@whoosh/design-system/src/components/display";

import {
  AgendaWaitlistRenderer,
  AgendaWaitlistRendererQueryString,
} from "./AgendaWaitlistRenderer";
import { AgendaWaitlistRendererQuery } from "./__generated__/AgendaWaitlistRendererQuery.graphql";

type TeeSheetWaitlistProps = {
  date: string;
  facilityId: string;
  courseIds: string[] | null;
  onJoinWaitlist?: (queueSlotId: string, requestedTime: string | null) => void;
};

export const AgendaWaitlist = ({
  date,
  facilityId,
  courseIds,
  onJoinWaitlist,
}: TeeSheetWaitlistProps) => {
  const [queryRef, loadQuery] = useQueryLoader<AgendaWaitlistRendererQuery>(
    AgendaWaitlistRendererQueryString
  );
  // Non-golf agendas pass an empty array that may be recreated by their parent.
  // Stabilize the query variable by value so those renders do not refetch.
  const serializedCourseIds = JSON.stringify(courseIds);
  const queryCourseIds = useMemo<string[] | null>(
    () => JSON.parse(serializedCourseIds) as string[] | null,
    [serializedCourseIds]
  );

  useEffect(() => {
    if (date) {
      loadQuery(
        {
          date,
          facilityId,
          courseIds: queryCourseIds,
        },
        { fetchPolicy: "network-only" }
      );
    }
  }, [loadQuery, date, facilityId, queryCourseIds]);

  return (
    <Suspense fallback={<Loader />}>
      {queryRef ? (
        <AgendaWaitlistRenderer
          queryRef={queryRef}
          onJoinWaitlist={onJoinWaitlist}
        />
      ) : null}
    </Suspense>
  );
};
